import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { db } from "../firebase";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, orderBy, serverTimestamp, where } from "firebase/firestore";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaDownload, FaUpload, FaSearch, FaFileExcel, FaTrashAlt, FaEdit, FaTimes, FaMapMarkerAlt } from "react-icons/fa";

export default function DsOutletMaster() {
  const [outlets, setOutlets] = useState([]);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterPartyId, setFilterPartyId] = useState(""); 
  const [fetchingOutlets, setFetchingOutlets] = useState(false);

  const [form, setForm] = useState({
    partyId: "",
    outletName: "",
    address: "",
    landmark: "",
    phone: "",
    yearlyLimit: "",
    status: "active",
  });

  const fetchParties = async () => {
    setLoading(true);
    const snap = await getDocs(query(collection(db, "partyMaster"), where("partytype", "==", "hawker"), orderBy("partyId")));
    setParties(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  const fetchOutlets = async (pid) => {
    const targetPid = pid || filterPartyId;
    setFetchingOutlets(true);
    try {
      let q = query(collection(db, "DsOutletMaster"), orderBy("outletName"));
      const snap = await getDocs(q);
      let arr = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      if (targetPid) {
        arr = arr.filter(o => o.partyId === targetPid);
      }
      setOutlets(arr);
    } catch (err) {
      console.error(err);
      alert("Error fetching outlets");
    } finally {
      setFetchingOutlets(false);
    }
  };

  useEffect(() => {
    fetchParties();
    fetchOutlets();
  }, []);

  const saveDsOutlet = async () => {
    if (!form.partyId || !form.outletName) {
      alert("Please fill required fields (Salesman & Outlet Name)");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...form,
        yearlyLimit: Number(form.yearlyLimit || 0),
        updatedAt: serverTimestamp(),
      };

      if (editingId) {
        await updateDoc(doc(db, "DsOutletMaster", editingId), payload);
        alert("Outlet Updated Successfully!");
      } else {
        await addDoc(collection(db, "DsOutletMaster"), {
          ...payload,
          yearlyUsed: 0,
          createdAt: serverTimestamp(),
          location: null, // Locations are captured via mobile app
        });
        alert("Outlet Registered!");
      }

      setForm({
        partyId: "",
        outletName: "",
        address: "",
        landmark: "",
        phone: "",
        yearlyLimit: "",
        status: "active",
      });
      setEditingId(null);
      await fetchOutlets();
    } catch (err) {
      console.error(err);
      alert("Error saving outlet");
    } finally {
      setLoading(false);
    }
  };

  const editOutlet = (o) => {
    setEditingId(o.id);
    setForm({
      partyId: o.partyId,
      outletName: o.outletName,
      address: o.address || "",
      landmark: o.landmark || "",
      phone: o.phone || "",
      yearlyLimit: o.yearlyLimit || "",
      status: o.status || "active",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteOutlet = async (id) => {
    if (!window.confirm("Permanently delete this outlet?")) return;
    await deleteDoc(doc(db, "DsOutletMaster", id));
    fetchOutlets();
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const rows = results.data;
          let count = 0;
          for (const row of rows) {
            if (!row.outletName || !row.partyId) continue;
            await addDoc(collection(db, "DsOutletMaster"), {
              partyId: row.partyId.trim().toUpperCase(),
              outletName: row.outletName.trim(),
              address: row.address || "",
              landmark: row.landmark || "",
              phone: row.phone || "",
              yearlyLimit: Number(row.yearlyLimit || 0),
              yearlyUsed: 0,
              status: "active",
              location: null,
              createdAt: serverTimestamp(),
            });
            count++;
          }
          alert(`Success! ${count} outlets imported.`);
          fetchOutlets();
        } catch (err) {
          alert("Import failed: " + err.message);
        } finally {
          setUploading(false);
          e.target.value = null;
        }
      }
    });
  };

  const downloadTemplate = () => {
    const data = [{
      partyId: "SALESMAN_ID",
      outletName: "SHOP NAME",
      address: "STREET NAME",
      landmark: "NEAR BY",
      phone: "1234567890",
      yearlyLimit: "50000"
    }];
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "DsOutlet_Template.xlsx");
  };

  const filteredOutlets = outlets.filter(o => 
    o.outletName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.partyId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div style={{ padding: "20px", fontFamily: "'Inter', sans-serif" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
          <div>
             <h1 style={{ margin: 0, color: "#1e293b" }}>Ds Outlet Master</h1>
             <p style={{ margin: 0, color: "#64748b", fontSize: "14px" }}>Manage isolated Field Billing outlets and limits.</p>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
             <button onClick={downloadTemplate} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", padding: "10px 20px", borderRadius: "10px", display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", cursor: "pointer" }}>
                <FaDownload /> Download Template
             </button>
             <label style={{ background: "#2563eb", color: "#fff", padding: "10px 20px", borderRadius: "10px", display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", cursor: "pointer" }}>
                <FaUpload /> {uploading ? "Uploading..." : "Bulk Upload"}
                <input type="file" hidden accept=".csv,.xlsx" onChange={handleBulkUpload} disabled={uploading} />
             </label>
          </div>
        </div>

        {/* REGISTRATION FORM */}
        <div style={{ background: "#fff", padding: "25px", borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", marginBottom: "30px" }}>
          <h3 style={{ marginTop: 0, marginBottom: "20px" }}>{editingId ? "Edit Outlet" : "Register New Outlet"}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px" }}>
             <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", color: "#64748b", marginBottom: "5px" }}>SALESMAN</label>
                <select value={form.partyId} onChange={e=>setForm({...form, partyId: e.target.value})} style={{ width: "100%", padding: "12px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                   <option value="">-- SELECT SALESMAN --</option>
                   {parties.map(p => <option key={p.partyId} value={p.partyId}>{p.name} ({p.partyId})</option>)}
                </select>
             </div>
             <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", color: "#64748b", marginBottom: "5px" }}>OUTLET NAME</label>
                <input type="text" placeholder="Shop Name" value={form.outletName} onChange={e=>setForm({...form, outletName: e.target.value})} style={{ width: "100%", padding: "12px", borderRadius: "10px", border: "1px solid #e2e8f0" }} />
             </div>
             <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", color: "#64748b", marginBottom: "5px" }}>LANDMARK</label>
                <input type="text" placeholder="Near by..." value={form.landmark} onChange={e=>setForm({...form, landmark: e.target.value})} style={{ width: "100%", padding: "12px", borderRadius: "10px", border: "1px solid #e2e8f0" }} />
             </div>
             <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", color: "#64748b", marginBottom: "5px" }}>YEARLY LIMIT (₹)</label>
                <input type="number" placeholder="Eg: 50000" value={form.yearlyLimit} onChange={e=>setForm({...form, yearlyLimit: e.target.value})} style={{ width: "100%", padding: "12px", borderRadius: "10px", border: "1px solid #e2e8f0" }} />
             </div>
             <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", color: "#64748b", marginBottom: "5px" }}>MOBILE</label>
                <input type="text" placeholder="10 digits" value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})} style={{ width: "100%", padding: "12px", borderRadius: "10px", border: "1px solid #e2e8f0" }} />
             </div>
             <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", color: "#64748b", marginBottom: "5px" }}>STATUS</label>
                <select value={form.status} onChange={e=>setForm({...form, status: e.target.value})} style={{ width: "100%", padding: "12px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                   <option value="active">Active</option>
                   <option value="disabled">Disabled</option>
                </select>
             </div>
          </div>
          <div style={{ marginTop: "20px" }}>
             <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", color: "#64748b", marginBottom: "5px" }}>FULL ADDRESS</label>
             <textarea rows="2" value={form.address} onChange={e=>setForm({...form, address: e.target.value})} style={{ width: "100%", padding: "12px", borderRadius: "10px", border: "1px solid #e2e8f0", resize: "none" }}></textarea>
          </div>
          <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
             <button onClick={saveDsOutlet} disabled={loading} style={{ background: "#2563eb", color: "#fff", border: "none", padding: "12px 30px", borderRadius: "10px", fontWeight: "bold", cursor: "pointer" }}>{loading ? "Saving..." : editingId ? "Update Outlet" : "Register Outlet"}</button>
             {editingId && <button onClick={()=>{ setEditingId(null); setForm({ partyId:"", outletName:"", address:"", landmark:"", phone:"", yearlyLimit:"", status:"active" }); }} style={{ background: "#f1f5f9", border: "none", padding: "12px 20px", borderRadius: "10px", fontWeight: "bold", cursor: "pointer" }}>Cancel</button>}
          </div>
        </div>

        {/* LISTING & FILTERS */}
        <div style={{ background: "#fff", borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", overflow: "hidden" }}>
           <div style={{ padding: "20px", display: "flex", justifyContent: "space-between", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <div style={{ position: "relative", width: "300px" }}>
                 <FaSearch style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                 <input type="text" placeholder="Search by name or salesman..." value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} style={{ width: "100%", padding: "10px 10px 10px 35px", borderRadius: "8px", border: "1px solid #e2e8f0" }} />
              </div>
              <select value={filterPartyId} onChange={e=>setFilterPartyId(e.target.value)} style={{ padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                 <option value="">-- ALL SALESMEN --</option>
                 {parties.map(p => <option key={p.partyId} value={p.partyId}>{p.name} ({p.partyId})</option>)}
              </select>
           </div>
           
           <div style={{ overflowX: "auto" }}>
              <table width="100%" style={{ borderCollapse: "collapse", textAlign: "left" }}>
                 <thead>
                    <tr style={{ background: "#f1f5f9", color: "#475569", fontSize: "12px" }}>
                       <th style={{ padding: "15px" }}>OUTLET / LANDMARK</th>
                       <th style={{ padding: "15px" }}>SALESMAN</th>
                       <th style={{ padding: "15px" }}>LOC</th>
                       <th style={{ padding: "15px", textAlign: "right" }}>YEARLY LIMIT</th>
                       <th style={{ padding: "15px", textAlign: "right" }}>CONSUMED</th>
                       <th style={{ padding: "15px", textAlign: "center" }}>STATUS</th>
                       <th style={{ padding: "15px", textAlign: "right" }}>ACTIONS</th>
                    </tr>
                 </thead>
                 <tbody>
                    {filteredOutlets.map(o => (
                       <tr key={o.id} style={{ borderBottom: "1px solid #f1f5f9", fontSize: "14px" }}>
                          <td style={{ padding: "15px" }}>
                             <div style={{ fontWeight: "bold", color: "#1e293b" }}>{o.outletName}</div>
                             <div style={{ fontSize: "11px", color: "#64748b" }}>{o.landmark || "No Landmark"} | {o.phone}</div>
                          </td>
                          <td style={{ padding: "15px" }}>{o.partyId}</td>
                          <td style={{ padding: "15px" }}>
                             {o.location ? <FaMapMarkerAlt color="#ef4444" title={`lat: ${o.location.lat}`} /> : <FaMapMarkerAlt color="#cbd5e1" title="No Location" />}
                          </td>
                          <td style={{ padding: "15px", textAlign: "right", fontWeight: "600" }}>₹{o.yearlyLimit?.toLocaleString()}</td>
                          <td style={{ padding: "15px", textAlign: "right", color: (o.yearlyUsed || 0) >= (o.yearlyLimit || 0) ? "red" : "#22c55e", fontWeight: "bold" }}>₹{(o.yearlyUsed || 0).toLocaleString()}</td>
                          <td style={{ padding: "15px", textAlign: "center" }}>
                             <span style={{ padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "bold", background: o.status === "active" ? "#dcfce7" : "#fee2e2", color: o.status === "active" ? "#166534" : "#991b1b" }}>{o.status?.toUpperCase()}</span>
                          </td>
                          <td style={{ padding: "15px", textAlign: "right" }}>
                             <button onClick={()=>editOutlet(o)} style={{ background: "none", border: "none", color: "#2563eb", cursor: "pointer", marginRight: "10px" }}><FaEdit /></button>
                             <button onClick={()=>deleteOutlet(o.id)} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer" }}><FaTrashAlt /></button>
                          </td>
                       </tr>
                    ))}
                 </tbody>
              </table>
           </div>
        </div>
      </div>
    </Layout>
  );
}
